import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Circle,
  Lock,
  Play,
} from "lucide-react";
import { api, LOGO, rupiah, youtubeId } from "../lib/api";
import { Loading } from "../components/Shell";
import AiChatWidget from "../components/AiChatWidget";

export function CoursePlayer() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api(`/me/course/${slug}`)
      .then(setData)
      .catch((e) => setErr(e.response?.data?.detail || "Kursus tidak dapat dibuka."));
  }, [slug]);
  if (err) {
    return (
      <div className="player-error" data-testid="player-error">
        <p>{err}</p>
        <Link to="/courses" className="button">
          Kembali ke katalog
        </Link>
      </div>
    );
  }
  if (!data) return <Loading label="Memuat ruang kelas..." />;
  const firstLesson = data.course.modules
    ?.flatMap((m) => m.lessons || [])
    .find((l) => l.unlocked);
  return (
    <div className="course-hub">
      <header className="course-hub-header">
        <Link to="/dashboard" className="back-link" data-testid="hub-back-link">
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <img src={LOGO} alt="MASTOFA.COM" />
        <span data-testid="hub-progress">{data.progress}% selesai</span>
      </header>
      <main className="course-hub-main">
        <div className="hub-intro">
          <span className="section-kicker">{data.course.level}</span>
          <h1>{data.course.title}</h1>
          <p>{data.course.description}</p>
          {firstLesson ? (
            <button
              className="button"
              data-testid="continue-learning-button"
              onClick={() => nav(`/learn/${slug}/${firstLesson.id}`)}
            >
              Lanjutkan belajar <ArrowRight size={17} />
            </button>
          ) : null}
        </div>
        <section className="hub-modules">
          {data.course.modules?.map((m, i) => (
            <div key={m.id} className="hub-module">
              <div className="hub-module-head">
                <span>{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <b>{m.title}</b>
                  <small>{(m.lessons || []).length} materi</small>
                </div>
              </div>
              <div className="hub-lessons">
                {(m.lessons || []).map((l) => (
                  <button
                    key={l.id}
                    className="hub-lesson"
                    disabled={!l.unlocked}
                    data-testid={`hub-lesson-${l.id}`}
                    onClick={() => nav(`/learn/${slug}/${l.id}`)}
                  >
                    {l.completed ? (
                      <CheckCircle2 size={16} className="ok" />
                    ) : l.unlocked ? (
                      <Circle size={16} />
                    ) : (
                      <Lock size={14} />
                    )}
                    <span>{l.title}</span>
                    <small>{l.duration}</small>
                  </button>
                ))}
                {(m.lessons || []).length === 0 && (
                  <div className="hub-empty">Materi akan segera hadir.</div>
                )}
              </div>
            </div>
          ))}
        </section>
      </main>
    </div>
  );
}

function ContentBlock({ block }) {
  const { type, data } = block;
  switch (type) {
    case "heading":
      return <h3 className="block-heading">{data.text}</h3>;
    case "text":
      return <p className="block-text">{data.text}</p>;
    case "image":
      return data.url ? (
        <img className="block-image" src={data.url} alt={data.caption || ""} />
      ) : null;
    case "youtube": {
      const id = youtubeId(data.url);
      return id ? (
        <div className="block-video">
          <iframe
            src={`https://www.youtube.com/embed/${id}`}
            title="Video"
            allowFullScreen
          />
        </div>
      ) : null;
    }
    case "quote":
      return (
        <blockquote className="block-quote">“{data.text}”</blockquote>
      );
    case "callout":
      return <div className="block-callout">{data.text}</div>;
    case "example":
      return (
        <div className="block-example">
          <b>Contoh</b>
          <p>{data.text}</p>
        </div>
      );
    case "checklist":
      return (
        <ul className="block-checklist">
          {(data.items || []).map((i, ix) => (
            <li key={ix}>
              <CheckCircle2 size={15} /> {i}
            </li>
          ))}
        </ul>
      );
    case "key_takeaway":
      return (
        <div className="block-takeaway">
          <span>KEY TAKEAWAY</span>
          <p>{data.text}</p>
        </div>
      );
    default:
      return null;
  }
}

function Quiz({ quiz, onDone }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const submit = async () => {
    try {
      const r = await api(`/me/quiz/${quiz.id}/attempt`, {
        method: "POST",
        data: { answers },
      });
      setResult(r);
      if (r.passed) onDone?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Gagal mengirim quiz.");
    }
  };
  if (result) {
    return (
      <div className="quiz-result" data-testid="quiz-result">
        <b>Skor: {result.score}</b>
        <p>{result.passed ? "Selamat, Anda lulus!" : "Belum lulus. Coba lagi."}</p>
        <button
          className="button"
          data-testid="quiz-retry-button"
          onClick={() => {
            setResult(null);
            setAnswers({});
          }}
        >
          Coba lagi
        </button>
      </div>
    );
  }
  return (
    <div className="quiz-box">
      <h3>{quiz.title}</h3>
      {quiz.questions?.map((q, idx) => (
        <div key={q.id} className="quiz-question" data-testid={`quiz-q-${q.id}`}>
          <b>
            {idx + 1}. {q.question}
          </b>
          <div className="quiz-options">
            {(q.options || []).map((opt) => (
              <label key={opt}>
                <input
                  type="radio"
                  name={q.id}
                  value={opt}
                  checked={answers[q.id] === opt}
                  onChange={() => setAnswers({ ...answers, [q.id]: opt })}
                />
                {opt}
              </label>
            ))}
          </div>
        </div>
      ))}
      {error && <div className="error">{error}</div>}
      <button
        className="button"
        onClick={submit}
        data-testid="quiz-submit-button"
      >
        Kirim jawaban
      </button>
    </div>
  );
}

function Assignment({ assignment }) {
  const [content, setContent] = useState("");
  const [msg, setMsg] = useState("");
  const submit = async () => {
    try {
      await api(`/me/assignment/${assignment.id}/submit`, {
        method: "POST",
        data: { content },
      });
      setMsg("Tugas berhasil dikirim.");
    } catch (e) {
      setMsg(e.response?.data?.detail || "Gagal mengirim.");
    }
  };
  return (
    <div className="assign-box">
      <h3>{assignment.title}</h3>
      <p>{assignment.instructions}</p>
      <textarea
        data-testid="assignment-input"
        rows="5"
        placeholder="Tulis jawaban Anda..."
        value={content}
        onChange={(e) => setContent(e.target.value)}
      />
      <button
        className="button"
        onClick={submit}
        data-testid="assignment-submit-button"
      >
        Kirim tugas
      </button>
      {msg && <small className="assign-msg">{msg}</small>}
    </div>
  );
}

export function LessonView() {
  const { slug, lessonId } = useParams();
  const nav = useNavigate();
  const [course, setCourse] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = () => {
    api(`/me/course/${slug}`).then(setCourse).catch(() => {});
    api(`/me/lesson/${lessonId}`)
      .then(setData)
      .catch((e) => setErr(e.response?.data?.detail || "Materi tidak tersedia."));
  };
  useEffect(() => {
    load();
  }, [slug, lessonId]);
  if (err)
    return (
      <div className="player-error">
        <p>{err}</p>
        <Link to="/dashboard" className="button">
          Kembali
        </Link>
      </div>
    );
  if (!data || !course) return <Loading label="Memuat materi..." />;
  const lesson = data.lesson;
  const ytId = youtubeId(lesson.youtube_url);
  const completed = data.progress?.status === "COMPLETED";
  const markComplete = async () => {
    try {
      await api(`/me/lesson/${lessonId}/complete`, { method: "POST" });
      load();
    } catch (e) {
      /* keep silent, UI already reflects state */
    }
  };
  return (
    <div className="lesson-page">
      <aside className="lesson-side" data-testid="lesson-sidebar">
        <Link to={`/learn/${slug}`} className="back-link" data-testid="lesson-back-link">
          <ArrowLeft size={16} /> {course.course.title}
        </Link>
        {/* Overall course progress bar */}
        {(() => {
          const allLessons = (course.course.modules || []).flatMap((m) => m.lessons || []);
          const doneCount = allLessons.filter((l) => l.completed).length;
          const total = allLessons.length;
          const pct = total ? Math.round((doneCount / total) * 100) : 0;
          return (
            <div style={{ padding: "8px 12px", marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 6, color: "#64748b" }}>
                <span>Progress kursus</span>
                <span><b style={{ color: "#0f172a" }}>{doneCount}/{total}</b> · {pct}%</span>
              </div>
              <div style={{ background: "#e2e8f0", height: 6, borderRadius: 3, overflow: "hidden" }}>
                <div style={{ background: "#10b981", width: `${pct}%`, height: "100%", transition: "width 0.4s" }} />
              </div>
            </div>
          );
        })()}
        <div className="lesson-list">
          {course.course.modules?.map((m) => {
            const modLessons = m.lessons || [];
            const modDone = modLessons.filter((l) => l.completed).length;
            const modTotal = modLessons.length;
            const modPct = modTotal ? Math.round((modDone / modTotal) * 100) : 0;
            return (
              <div key={m.id}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px 4px" }}>
                  <span className="side-label" style={{ fontSize: 12, fontWeight: 700 }}>{m.title}</span>
                  <span style={{ fontSize: 10, color: modPct === 100 ? "#10b981" : "#94a3b8", fontWeight: 600 }}>
                    {modDone}/{modTotal}
                  </span>
                </div>
                {modTotal > 0 && (
                  <div style={{ background: "#f1f5f9", height: 2, margin: "0 12px 6px", borderRadius: 1, overflow: "hidden" }}>
                    <div style={{ background: modPct === 100 ? "#10b981" : "#3b82f6", width: `${modPct}%`, height: "100%" }} />
                  </div>
                )}
                {modLessons.map((l) => {
                  const isCurrent = l.id === lessonId;
                  const status = l.completed ? "done" : isCurrent ? "current" : l.unlocked ? "todo" : "locked";
                  return (
                    <button
                      key={l.id}
                      className={isCurrent ? "active" : ""}
                      disabled={!l.unlocked}
                      data-testid={`side-lesson-${l.id}`}
                      onClick={() => nav(`/learn/${slug}/${l.id}`)}
                      title={l.completed ? "Selesai" : isCurrent ? "Sedang dipelajari" : l.unlocked ? "Belum selesai" : "Terkunci"}
                    >
                      {status === "done" && <CheckCircle2 size={14} className="ok" style={{ color: "#10b981" }} />}
                      {status === "current" && <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "#3b82f6", boxShadow: "0 0 0 3px rgba(59,130,246,0.2)" }} />}
                      {status === "todo" && <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", border: "2px solid #cbd5e1" }} />}
                      {status === "locked" && <Lock size={12} />}
                      <span style={{ fontWeight: isCurrent ? 600 : 400 }}>{l.title}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </aside>
      <main className="lesson-main">
        {/* Breadcrumb */}
        <div style={{ fontSize: "0.8rem", color: "#64748b", marginBottom: 8, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }} data-testid="lesson-breadcrumb">
          <Link to="/belajar" style={{ color: "#64748b", textDecoration: "none" }}>Belajar</Link>
          <ChevronRight size={12} />
          <Link to={`/learn/${slug}`} style={{ color: "#64748b", textDecoration: "none" }}>
            {course.course.title}
          </Link>
          <ChevronRight size={12} />
          <span style={{ color: "#0f172a" }}>{lesson.title}</span>
        </div>

        <span className="section-kicker">MATERI</span>
        <h1>{lesson.title}</h1>
        {lesson.description && <p className="lesson-desc">{lesson.description}</p>}

        {/* Sprint 5: Prerequisite Soft Warning + Actions (dismissable, non-blocking) */}
        {data.prerequisites?.length > 0 && data.prerequisites.some((p) => !p.completed) && (
          <PrereqWarning
            lessonId={lessonId}
            slug={slug}
            prerequisites={data.prerequisites}
            nav={nav}
          />
        )}

        {/* SECTION 1: TUJUAN PEMBELAJARAN */}
        {lesson.learning_objectives?.length ? (
          <div className="lesson-obj" data-testid="lesson-objectives" style={{ marginBottom: 24 }}>
            <b>🎯 Tujuan pembelajaran</b>
            <ul>
              {lesson.learning_objectives.map((o, i) => (
                <li key={i}><CheckCircle2 size={15} /> {o}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {/* SECTION 2: MATERI (Video + Content Blocks) */}
        {ytId ? (
          <div className="block-video">
            <iframe
              src={`https://www.youtube.com/embed/${ytId}`}
              title={lesson.title}
              allowFullScreen
              data-testid="lesson-youtube-iframe"
            />
          </div>
        ) : lesson.youtube_url ? (
          <div className="error">Link YouTube tidak valid.</div>
        ) : null}

        <div className="lesson-blocks" data-testid="lesson-blocks">
          {(lesson.content_blocks || []).map((b) => (
            <ContentBlock key={b.id} block={b} />
          ))}
        </div>

        {/* SECTION 3: KENAPA INI PENTING */}
        {lesson.why_important && (
          <div data-testid="lesson-why" style={{ background: "#eff6ff", padding: 20, borderRadius: 10, margin: "24px 0", borderLeft: "4px solid #2563eb" }}>
            <b style={{ display: "block", marginBottom: 8, color: "#1e40af" }}>💡 Kenapa ini penting?</b>
            <p style={{ margin: 0, color: "#334155", lineHeight: 1.6 }}>{lesson.why_important}</p>
          </div>
        )}

        {/* SECTION 4: CONTOH */}
        {lesson.example && (
          <div data-testid="lesson-example" style={{ background: "#f0fdf4", padding: 20, borderRadius: 10, margin: "24px 0", borderLeft: "4px solid #10b981" }}>
            <b style={{ display: "block", marginBottom: 8, color: "#065f46" }}>📌 Contoh</b>
            <p style={{ margin: 0, color: "#334155", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{lesson.example}</p>
          </div>
        )}

        {/* SECTION 5: STEP-BY-STEP PRAKTIK */}
        {lesson.steps?.length ? (
          <div data-testid="lesson-steps" style={{ background: "#fff", border: "1px solid #e2e8f0", padding: 20, borderRadius: 10, margin: "24px 0" }}>
            <b style={{ display: "block", marginBottom: 12 }}>🛠 Langkah praktik</b>
            <ol style={{ paddingLeft: 20, margin: 0, color: "#334155", lineHeight: 1.7 }}>
              {lesson.steps.map((s, i) => (<li key={i} style={{ marginBottom: 6 }}>{s}</li>))}
            </ol>
          </div>
        ) : null}

        {/* SECTION 6: KESALAHAN UMUM */}
        {lesson.common_mistakes?.length ? (
          <div data-testid="lesson-mistakes" style={{ background: "#fef2f2", padding: 20, borderRadius: 10, margin: "24px 0", borderLeft: "4px solid #ef4444" }}>
            <b style={{ display: "block", marginBottom: 8, color: "#991b1b" }}>⚠️ Kesalahan yang harus dihindari</b>
            <ul style={{ paddingLeft: 20, margin: 0, color: "#334155", lineHeight: 1.6 }}>
              {lesson.common_mistakes.map((m, i) => (<li key={i} style={{ marginBottom: 4 }}>{m}</li>))}
            </ul>
          </div>
        ) : null}

        {/* SECTION 7: CHECKLIST — DB-synced (Sprint 5) */}
        {lesson.checklist?.length ? (
          <LessonChecklist
            items={lesson.checklist}
            lessonId={lessonId}
            serverChecked={data.progress?.checklist_checked || []}
          />
        ) : null}

        {/* Sprint 5: Live Practice Templates — ROAS / Break-Even calculators */}
        {lesson.resources?.includes("roas_calculator") && <RoasCalculator />}
        {lesson.resources?.includes("breakeven_calculator") && <BreakEvenCalculator />}

        {/* SECTION 8: QUIZ */}
        {data.quiz && <Quiz quiz={data.quiz} onDone={markComplete} />}
        {data.assignment && <Assignment assignment={data.assignment} />}

        {/* SECTION 9: RINGKASAN */}
        {lesson.summary && (
          <div data-testid="lesson-summary" style={{ background: "#0f172a", color: "#f8fafc", padding: 24, borderRadius: 10, margin: "32px 0" }}>
            <b style={{ display: "block", marginBottom: 8, color: "#fbbf24" }}>📝 Ringkasan</b>
            <p style={{ margin: 0, lineHeight: 1.7 }}>{lesson.summary}</p>
          </div>
        )}

        {/* SECTION 10: NEXT LESSON — Full-width action footer */}
        <div
          data-testid="lesson-next-footer"
          style={{
            background: "linear-gradient(135deg,#1e40af,#3b82f6)",
            color: "#fff",
            padding: 24,
            borderRadius: 12,
            margin: "32px 0 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 20,
            flexWrap: "wrap",
          }}
        >
          <div style={{ minWidth: 240, flex: 1 }}>
            {data.next_lesson ? (
              <>
                <small style={{ opacity: 0.85, letterSpacing: 1, textTransform: "uppercase" }}>Setelah ini Anda akan belajar</small>
                <b style={{ display: "block", fontSize: "1.2rem", marginTop: 4, color: "#fff" }}>
                  {data.next_lesson.title}
                </b>
                {data.next_lesson.duration && (
                  <small style={{ opacity: 0.75 }}>⏱ {data.next_lesson.duration}</small>
                )}
              </>
            ) : (
              <>
                <small style={{ opacity: 0.85, letterSpacing: 1, textTransform: "uppercase" }}>🎉 Selamat!</small>
                <b style={{ display: "block", fontSize: "1.2rem", marginTop: 4 }}>
                  Anda sudah menyelesaikan semua materi di kursus ini
                </b>
              </>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {!completed && (
              <button
                onClick={markComplete}
                data-testid="lesson-complete-button"
                className="button"
                style={{ background: "#10b981", color: "#fff", fontWeight: 700 }}
              >
                <CheckCircle2 size={16} /> Tandai selesai
              </button>
            )}
            {data.next_lesson ? (
              <button
                data-testid="lesson-next-button"
                onClick={() => nav(`/learn/${slug}/${data.next_lesson.id}`)}
                className="button"
                style={{ background: "#fbbf24", color: "#0f172a", fontWeight: 700 }}
              >
                Lanjut ke materi berikutnya <ChevronRight size={16} />
              </button>
            ) : (
              <Link
                to="/dashboard"
                data-testid="lesson-done-course"
                className="button"
                style={{ background: "#fbbf24", color: "#0f172a", fontWeight: 700, textDecoration: "none" }}
              >
                Kembali ke Dashboard <ArrowLeft size={16} />
              </Link>
            )}
          </div>
        </div>

        {/* Prev button — smaller, secondary */}
        {data.prev_lesson && (
          <div style={{ textAlign: "left", marginTop: 8 }}>
            <button
              data-testid="lesson-prev-button"
              onClick={() => nav(`/learn/${slug}/${data.prev_lesson.id}`)}
              className="button-ghost button-small"
            >
              <ChevronLeft size={14} /> Sebelumnya: {data.prev_lesson.title}
            </button>
          </div>
        )}
      </main>
      <AiChatWidget lessonId={lessonId} buttonLabel="Tanya AI tentang lesson" />
    </div>
  );
}

// Lesson Checklist — DB-synced (Sprint 5). Server = source of truth.
// localStorage acts as an offline cache to avoid flicker before server response.
function LessonChecklist({ items, lessonId, serverChecked }) {
  const storageKey = `mastofa_checklist_${lessonId}`;
  const [checked, setChecked] = useState(() => {
    // Prefer server state on first render if provided; else localStorage
    if (Array.isArray(serverChecked) && serverChecked.length > 0) {
      return [...serverChecked];
    }
    try {
      return JSON.parse(localStorage.getItem(storageKey) || "[]");
    } catch {
      return [];
    }
  });
  // Whenever server sends fresh state (route change / cross-device), reconcile.
  useEffect(() => {
    if (Array.isArray(serverChecked)) {
      const sorted = [...serverChecked].sort((a, b) => a - b);
      const local = [...checked].sort((a, b) => a - b);
      if (JSON.stringify(sorted) !== JSON.stringify(local)) {
        setChecked(sorted);
      }
    }
  }, [serverChecked?.join(",")]);
  // Persist to localStorage as offline cache
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(checked));
  }, [checked, storageKey]);
  const toggle = async (i) => {
    const next = checked.includes(i)
      ? checked.filter((x) => x !== i)
      : [...checked, i];
    setChecked(next);
    // Fire-and-forget DB sync (idempotent).
    try {
      await api(`/me/lesson/${lessonId}/progress`, {
        method: "POST",
        data: { checklist_checked: next },
      });
    } catch {
      /* offline: localStorage still holds latest; will resync on next visit */
    }
  };
  return (
    <div
      data-testid="lesson-checklist"
      style={{ background: "#fff", border: "1px solid #e2e8f0", padding: 20, borderRadius: 10, margin: "24px 0" }}
    >
      <b style={{ display: "block", marginBottom: 12 }}>
        ✅ Checklist ({checked.length}/{items.length})
      </b>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((it, i) => (
          <label
            key={i}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              cursor: "pointer", padding: 8, borderRadius: 6,
              background: checked.includes(i) ? "#f0fdf4" : "transparent",
            }}
            data-testid={`checklist-item-${i}`}
          >
            <input
              type="checkbox"
              checked={checked.includes(i)}
              onChange={() => toggle(i)}
              style={{ width: 18, height: 18 }}
            />
            <span style={{ textDecoration: checked.includes(i) ? "line-through" : "none", color: checked.includes(i) ? "#64748b" : "#334155" }}>
              {it}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

// Sprint 5: Prerequisite soft warning — non-blocking, with CTAs.
// "Pelajari Prerequisite" jumps to first uncompleted prereq.
// "Saya Sudah Paham — Lanjut" dismisses the warning (per-lesson, remembered).
function PrereqWarning({ lessonId, slug, prerequisites, nav }) {
  const dismissKey = `mastofa_prereq_ack_${lessonId}`;
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(dismissKey) === "1";
    } catch {
      return false;
    }
  });
  if (dismissed) return null;
  const firstIncomplete = prerequisites.find((p) => !p.completed);
  return (
    <div
      data-testid="lesson-prereq-banner"
      style={{
        background: "#fef3c7",
        border: "1px solid #fbbf24",
        padding: 18,
        borderRadius: 10,
        margin: "16px 0",
      }}
    >
      <b style={{ color: "#78350f", display: "block", marginBottom: 6, fontSize: "0.95rem" }}>
        📚 Sebelum mempelajari ini
      </b>
      <p style={{ margin: "0 0 12px", color: "#78350f", fontSize: "0.9rem", lineHeight: 1.55 }}>
        Untuk memahami materi ini dengan lebih baik, kami menyarankan Anda menyelesaikan
        materi berikut terlebih dahulu.
      </p>
      <ul
        style={{
          paddingLeft: 20,
          margin: "0 0 14px",
          color: "#78350f",
          fontSize: "0.9rem",
          listStyle: "none",
        }}
        data-testid="lesson-prereq-list"
      >
        {prerequisites.map((p) => (
          <li key={p.id} style={{ marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}>
            <span
              aria-hidden
              style={{
                color: p.completed ? "#059669" : "#94a3b8",
                fontWeight: 800,
              }}
            >
              {p.completed ? "✓" : "○"}
            </span>
            <span>{p.title}</span>
          </li>
        ))}
      </ul>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        {firstIncomplete && (
          <button
            type="button"
            data-testid="lesson-prereq-study-btn"
            onClick={() => nav(`/learn/${slug}/${firstIncomplete.id}`)}
            style={{
              background: "#78350f",
              color: "#fff",
              border: 0,
              padding: "10px 16px",
              borderRadius: 6,
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <BookOpen size={14} /> Pelajari Prerequisite
          </button>
        )}
        <button
          type="button"
          data-testid="lesson-prereq-bypass-btn"
          onClick={() => {
            try {
              localStorage.setItem(dismissKey, "1");
            } catch {
              /* ignore storage errors */
            }
            setDismissed(true);
          }}
          style={{
            background: "transparent",
            color: "#78350f",
            border: "1px solid #78350f",
            padding: "10px 16px",
            borderRadius: 6,
            fontWeight: 700,
            fontSize: "0.85rem",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          Saya Sudah Paham — Lanjut <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Live Practice Templates (Sprint 5 · Level 4)
// ROAS + Break-Even calculators — formulas consistent with MASTOFA material.
// ---------------------------------------------------------------------------
const rupiahFmt = (n) =>
  isFinite(n) ? "Rp " + Math.round(n).toLocaleString("id-ID") : "-";
const num = (v) => {
  const n = Number(String(v).replace(/[^0-9.-]/g, ""));
  return isFinite(n) ? n : 0;
};

function CalcInput({ label, value, onChange, suffix, testid }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13, color: "#334155", fontWeight: 600 }}>
      {label}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <input
          type="text" inputMode="decimal" value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          style={{ flex: 1, padding: "10px 12px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 14, fontFamily: "inherit", background: "#fff" }}
        />
        {suffix && <span style={{ color: "#64748b", fontSize: 12 }}>{suffix}</span>}
      </div>
    </label>
  );
}

function CalcOutput({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} style={{ background: "#fff", border: `1px solid ${accent}33`, borderLeft: `4px solid ${accent}`, padding: "12px 16px", borderRadius: 6, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
      <span style={{ fontSize: 13, color: "#475569", fontWeight: 600 }}>{label}</span>
      <b style={{ fontSize: 16, color: accent, fontWeight: 800 }}>{value}</b>
    </div>
  );
}

function RoasCalculator() {
  const [sellingPrice, setSellingPrice] = useState("100000");
  const [hpp, setHpp] = useState("60000");
  const [platformFee, setPlatformFee] = useState("8");
  const [adCost, setAdCost] = useState("20000");
  const p = num(sellingPrice), h = num(hpp), feePct = num(platformFee), ad = num(adCost);
  const feeRupiah = (p * feePct) / 100;
  const grossMargin = Math.max(0, p - h - feeRupiah);
  const breakEvenRoas = grossMargin > 0 ? p / grossMargin : 0;
  const targetRoas = breakEvenRoas * 1.5;
  const profitPerSale = grossMargin - ad;
  const actualRoas = ad > 0 ? p / ad : 0;
  return (
    <div data-testid="lesson-roas-calculator" style={{ background: "linear-gradient(135deg,#f0f9ff 0%,#fff 100%)", border: "1px solid #93c5fd", borderRadius: 12, padding: 24, margin: "24px 0" }}>
      <div style={{ marginBottom: 14 }}>
        <b style={{ fontSize: "1.05rem", color: "#0f172a" }}>📈 Kalkulator ROAS</b>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: "#475569" }}>
          Hitung Break-Even ROAS &amp; target ROAS berdasarkan margin Anda sendiri. Semua angka dalam Rupiah (kecuali persentase).
        </p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12, marginBottom: 16 }}>
        <CalcInput label="Harga Jual per unit" value={sellingPrice} onChange={setSellingPrice} suffix="Rp" testid="roas-input-price" />
        <CalcInput label="HPP per unit" value={hpp} onChange={setHpp} suffix="Rp" testid="roas-input-hpp" />
        <CalcInput label="Potongan Platform" value={platformFee} onChange={setPlatformFee} suffix="%" testid="roas-input-fee" />
        <CalcInput label="Biaya Iklan per sale" value={adCost} onChange={setAdCost} suffix="Rp" testid="roas-input-adcost" />
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        <CalcOutput label="Margin Kotor per sale" value={rupiahFmt(grossMargin)} accent="#2563eb" testid="roas-output-margin" />
        <CalcOutput label="Break-Even ROAS" value={breakEvenRoas > 0 ? `${breakEvenRoas.toFixed(2)}x` : "-"} accent="#ef4444" testid="roas-output-be" />
        <CalcOutput label="Target ROAS Minimum (Break-Even × 1.5)" value={targetRoas > 0 ? `${targetRoas.toFixed(2)}x` : "-"} accent="#f59e0b" testid="roas-output-target" />
        <CalcOutput label="ROAS aktual (dari biaya iklan Anda)" value={actualRoas > 0 ? `${actualRoas.toFixed(2)}x` : "-"} accent="#10b981" testid="roas-output-actual" />
        <CalcOutput label="Profit per sale (Margin − Iklan)" value={rupiahFmt(profitPerSale)} accent={profitPerSale >= 0 ? "#10b981" : "#dc2626"} testid="roas-output-profit" />
      </div>
      <small style={{ display: "block", marginTop: 12, color: "#64748b", fontSize: 12, lineHeight: 1.5 }}>
        Formula: Break-Even ROAS = Harga Jual ÷ (Harga Jual − HPP − Potongan Platform). Angka di atas adalah <b>Contoh Simulasi</b> — ganti dengan angka bisnis Anda.
      </small>
    </div>
  );
}

function BreakEvenCalculator() {
  const [sellingPrice, setSellingPrice] = useState("100000");
  const [hpp, setHpp] = useState("60000");
  const [platformFee, setPlatformFee] = useState("8");
  const [targetProfit, setTargetProfit] = useState("10000");
  const p = num(sellingPrice), h = num(hpp), feePct = num(platformFee), tp = num(targetProfit);
  const feeRupiah = (p * feePct) / 100;
  const grossMargin = Math.max(0, p - h - feeRupiah);
  const maxCpaBreakEven = grossMargin;
  const maxCpaWithProfit = Math.max(0, grossMargin - tp);
  const targetRoasForProfit = maxCpaWithProfit > 0 ? p / maxCpaWithProfit : 0;
  return (
    <div data-testid="lesson-breakeven-calculator" style={{ background: "linear-gradient(135deg,#fef3c7 0%,#fff 100%)", border: "1px solid #f59e0b", borderRadius: 12, padding: 24, margin: "24px 0" }}>
      <div style={{ marginBottom: 14 }}>
        <b style={{ fontSize: "1.05rem", color: "#0f172a" }}>⚖️ Kalkulator Break-Even</b>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: "#475569" }}>
          Tentukan batas maksimum biaya iklan per sale supaya campaign tetap untung sesuai target profit Anda.
        </p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12, marginBottom: 16 }}>
        <CalcInput label="Harga Jual per unit" value={sellingPrice} onChange={setSellingPrice} suffix="Rp" testid="be-input-price" />
        <CalcInput label="HPP per unit" value={hpp} onChange={setHpp} suffix="Rp" testid="be-input-hpp" />
        <CalcInput label="Potongan Platform" value={platformFee} onChange={setPlatformFee} suffix="%" testid="be-input-fee" />
        <CalcInput label="Target Profit per sale" value={targetProfit} onChange={setTargetProfit} suffix="Rp" testid="be-input-target" />
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        <CalcOutput label="Margin Kotor (sebelum iklan)" value={rupiahFmt(grossMargin)} accent="#2563eb" testid="be-output-margin" />
        <CalcOutput label="Batas CPA Break-Even (untung Rp 0)" value={rupiahFmt(maxCpaBreakEven)} accent="#ef4444" testid="be-output-be-cpa" />
        <CalcOutput label={`Batas CPA agar profit ≥ ${rupiahFmt(tp)}`} value={rupiahFmt(maxCpaWithProfit)} accent="#f59e0b" testid="be-output-target-cpa" />
        <CalcOutput label="Target ROAS agar profit tercapai" value={targetRoasForProfit > 0 ? `${targetRoasForProfit.toFixed(2)}x` : "Tidak mencapai target"} accent="#10b981" testid="be-output-target-roas" />
      </div>
      <small style={{ display: "block", marginTop: 12, color: "#64748b", fontSize: 12, lineHeight: 1.5 }}>
        Formula: Batas CPA = Margin Kotor − Target Profit; Target ROAS = Harga Jual ÷ Batas CPA. Angka default adalah <b>Contoh Simulasi</b> — sesuaikan dengan SKU Anda.
      </small>
    </div>
  );
}
